import React, { useEffect, useState, forwardRef, useContext } from 'react'

// react-router-dom components
import { useLocation, NavLink, Link, useNavigate } from 'react-router-dom'

// prop-types is a library for typechecking of props.
import PropTypes from 'prop-types'

// @mui material components
import List from '@mui/material/List'
import Divider from '@mui/material/Divider'
// import Link from '@mui/material/Link'
import Icon from '@mui/material/Icon'

// Soft UI Dashboard PRO React components
// import Box from 'components/Box'

// Soft UI Dashboard PRO React example components
import SidenavCollapse from 'components/Sidenav/SidenavCollapse'
import SidenavList from 'components/Sidenav/SidenavList'
import SidenavItem from 'components/Sidenav/SidenavItem'

// Custom styles for the Sidenav
import SidenavRoot from 'components/Sidenav/SidenavRoot'
import sidenavLogoLabel from 'components/Sidenav/styles/sidenav'

// Email Modal
import { ContactUsModalForm } from 'layouts/authenticated/pages/dashboard/components/ContactUsModalForm'

// Soft UI Dashboard PRO React context
import { useSoftUIController, setMiniSidenav } from 'context'

import { IntakeContext } from 'context/IntakeContext'

// icons and colors
import CallIcon from '@mui/icons-material/Call'
import HelpIcon from '@mui/icons-material/Help'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import colors from 'assets/theme/base/colors'

// Popup
import { callZapier } from 'utils/zapier/callZapier'
import {
  Box,
  Button,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Slide,
  Fade,
  Typography,
} from '@mui/material'
import { CardGiftcard, EventAvailable, Lock } from '@mui/icons-material'
import referralIcon from '../../assets/images/referral.svg'

import { ReferralModal } from 'components/Referral'
import pxToRem from 'assets/theme/functions/pxToRem'
import DashboardNavbar from 'components/DashboardNavbar'

import { selectData } from 'layouts/onboarding'
import logo from '../../assets/images/parco-logo.svg'
import { useModal } from 'components/modals'

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />
})

const Sidenav = forwardRef(function Sidenav(
  { brand, brandName, routes, ...rest },
  ref
) {
  const { isAdmin, isAgency, signOut, intakeState, unassume, assumedUser } =
    useContext(IntakeContext)
  const isGovernmentEmployee = intakeState.governmentEmployee
  const [openCollapse, setOpenCollapse] = useState(false)
  const [openNestedCollapse, setOpenNestedCollapse] = useState(false)
  const [controller, dispatch] = useSoftUIController()
  const { miniSidenav, transparentSidenav } = controller
  const location = useLocation()
  const navigate = useNavigate()
  const { pathname } = location
  const collapseName = pathname.split('/').slice(1)[0]
  const itemName = pathname.split('/').slice(1)[1]
  const [openContactUsModal, setOpenContactUsModal] = useState(false)
  const [openReferral, setOpenReferral] = useState(false)
  const [contactUsModalTitle, setContactUsModalTitle] = useState([
    'Contact The PARCO Team Via Email',
    'Email Subject',
    'Message for the PARCO team',
  ])
  const { openModal } = useModal()
  const [accountMenu, setAccountMenu] = useState(false)
  const handleToggleProfile = (e) => {
    setAccountMenu(e.currentTarget)
  }
  const accountMenuIsOpen = Boolean(accountMenu)
  const handleCloseAccountMenu = () => {
    setAccountMenu(null)
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true)
  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav)

  const sidebarHelp = () => {
    callZapier({
      user: intakeState,
      hookUrl: 'https://hooks.zapier.com/hooks/catch/15607509/38uefcd/',
      payload: {
        user: {
          firstName: intakeState.firstName,
          lastName: intakeState.lastName,
          phoneNumber: intakeState.phone,
          email: intakeState.email,
        },
      },
      sendNotification: true,
      notificationMessage:
        'A request has been sent to PARCO! Expect a call momentarily.',
    })
  }

  const handleContactUsModalOpen = () => {
    setContactUsModalTitle([
      'Contact The PARCO Team Via Email',
      'Email Subject',
      'Message for the PARCO team',
    ])
    setOpenContactUsModal(true)
  }

  const handleIssueReportModalOpen = () => {
    setContactUsModalTitle([
      'Report An Issue To The PARCO Team',
      'Email Subject',
      'Please describe the issue for the PARCO team',
    ])
    setOpenContactUsModal(true)
  }
  const handleContactUsModalClose = () => {
    setOpenContactUsModal(false)
  }

  const handleReferralClose = () => {
    setOpenReferral(false)
  }

  const handleReferralOpen = () => {
    openModal('referral')
  }

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenavHideMobile() {
      setMiniSidenav(dispatch, window.innerWidth < 992)
    }

    /**
     The event listener that's calling the handleMiniSidenavHideMobile function when resizing the window.
    */
    window.addEventListener('resize', handleMiniSidenavHideMobile)

    // Call the handleMiniSidenavHideMobile function to set the state with the initial value.
    handleMiniSidenavHideMobile()

    // Remove event listener on cleanup
    return () =>
      window.removeEventListener('resize', handleMiniSidenavHideMobile)
  }, [dispatch, location])

  // if the referral hash exists trigger on load
  useEffect(() => {
    if (location.hash === '#referral') {
      openModal('referral')

      // Remove the hash without reloading or pushing history
      navigate(location.pathname + location.search, { replace: true })
    }
  }, [location, openModal, navigate])

  // Render all the nested collapse items from the routes.js
  const renderNestedCollapse = (collapse) => {
    const template = collapse.map(({ name, to, route, key, href }) =>
      href ? (
        <Link
          key={key}
          href={href}
          target="_blank"
          rel="noreferrer"
          data-testid={`sidenavItem-${name}`}
          sx={{ textDecoration: 'none' }}
        >
          <SidenavItem name={name} nested />
        </Link>
      ) : (
        <NavLink
          to={to || route}
          key={key}
          sx={{ textDecoration: 'none' }}
          data-testid={`sidenavItem-${name}`}
        >
          <SidenavItem name={name} active={route === pathname} nested />
        </NavLink>
      )
    )

    return template
  }

  // Render the all the collpases from the routes.js
  const renderCollapse = (collapses) =>
    collapses.map(
      ({
        name,
        collapse,
        to,
        route,
        href,
        key,
        requiresAdmin,
        requiresAgency,
        noDisplay = false,
      }) => {
        if (
          noDisplay ||
          (requiresAdmin && !isAdmin) ||
          (requiresAgency && !isGovernmentEmployee)
        )
          return <React.Fragment key={key} />
        let returnValue

        if (collapse) {
          returnValue = (
            <SidenavItem
              key={key}
              name={name}
              active={key === itemName}
              open={openNestedCollapse === name}
              onClick={() =>
                openNestedCollapse === name
                  ? setOpenNestedCollapse(false)
                  : setOpenNestedCollapse(name)
              }
            >
              {renderNestedCollapse(collapse)}
            </SidenavItem>
          )
        } else {
          returnValue = href ? (
            <Link
              href={href}
              key={key}
              target="_blank"
              rel="noreferrer"
              sx={{ textDecoration: 'none' }}
            >
              <SidenavItem name={name} active={key === itemName} />
            </Link>
          ) : (
            <NavLink
              to={to || route}
              key={key}
              sx={{ textDecoration: 'none' }}
              data-testid={`sidenavItem-${name}`}
            >
              <SidenavItem name={name} active={key === itemName} />
            </NavLink>
          )
        }
        return <SidenavList key={key}>{returnValue}</SidenavList>
      }
    )

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(
    ({
      type,
      name,
      icon,
      title,
      collapse,
      noCollapse,
      key,
      href,
      route,
      requiresAdmin,
      requiresAgency,
      noDisplay = false,
    }) => {
      if (
        noDisplay ||
        (requiresAdmin && !isAdmin) ||
        (requiresAgency && !isGovernmentEmployee)
      )
        return <></>
      let returnValue

      if (type === 'collapse') {
        if (href) {
          returnValue = (
            <Link
              href={href}
              key={key}
              target="_blank"
              rel="noreferrer"
              sx={{ textDecoration: 'none' }}
            >
              <SidenavCollapse
                name={name}
                icon={icon}
                active={key === collapseName}
                noCollapse={noCollapse}
              />
            </Link>
          )
        } else if (noCollapse && route) {
          returnValue = (
            <NavLink to={route} key={key}>
              <SidenavCollapse
                name={name}
                icon={icon}
                noCollapse={noCollapse}
                active={key === collapseName}
              >
                {collapse ? renderCollapse(collapse) : null}
              </SidenavCollapse>
            </NavLink>
          )
        } else if (requiresAdmin) {
          returnValue = (
            <SidenavCollapse
              requiresAdmin={requiresAdmin}
              key={key}
              name={name}
              icon={icon}
              active={key === collapseName}
              open={openCollapse === key}
              onClick={() =>
                openCollapse === key
                  ? setOpenCollapse(false)
                  : setOpenCollapse(key)
              }
            >
              {collapse ? renderCollapse(collapse) : null}
            </SidenavCollapse>
          )
        } else {
          returnValue = (
            <SidenavCollapse
              key={key}
              name={name}
              icon={icon}
              active={key === collapseName}
              open={openCollapse === key}
              onClick={() =>
                openCollapse === key
                  ? setOpenCollapse(false)
                  : setOpenCollapse(key)
              }
            >
              {collapse ? renderCollapse(collapse) : null}
            </SidenavCollapse>
          )
        }
      } else if (type === 'title') {
        returnValue = (
          <Typography
            key={key}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            opacity={0.6}
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </Typography>
        )
      } else if (type === 'divider') {
        returnValue = <Divider key={key} />
      }

      return returnValue
    }
  )

  const agencyOrCompanyLogo = intakeState.governmentEmployee
    ? selectData.agency.find(({ value }) => value === intakeState.agency)
        ?.logo || ''
    : intakeState.employerDetails?.logo || ''
  const hasLogo = agencyOrCompanyLogo !== ''

  return (
    <SidenavRoot
      {...rest}
      data-testid="sidenav"
      variant="permanent"
      ownerState={{ transparentSidenav, miniSidenav }}
      ref={ref}
    >
      <IconButton
        id={'toggleMiniSideNavFloatingButton'}
        onClick={handleMiniSidenav}
        sx={{
          top: 10,
          color: '#fff',
          boxShadow: '0px 4px 12px 0px #00000033',
          backgroundColor: '#000',
          '&:hover': {
            backgroundColor: '#000',
          },
        }}
        data-testid="toggleMenuButton"
      >
        <Icon backgroundColor="#000" color="#FFF">
          {miniSidenav ? 'menu' : 'close'}
        </Icon>
      </IconButton>
      <Box
        id={'toggleMiniSideNavFloatingButton'}
        onClick={(e) => {
          {
            !miniSidenav && handleMiniSidenav(e)
          }
        }}
        sx={{
          top: 0,
          position: 'absolute',
          display: miniSidenav ? 'none' : 'flex',
          height: '100vh',
          width: '100vw',
          zIndex: -1,
          left: miniSidenav ? 275 : 300,
          backgroundColor: 'rgba(0, 0, 0, 0.15)', // translucent
        }}
      />
      <Box
        sx={{
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          overflow: 'hidden',
          overflowY: 'auto',
        }}
      >
        <ContactUsModalForm
          handleClose={handleContactUsModalClose}
          open={openContactUsModal}
          handleSubmit={handleContactUsModalClose}
          emailDialogParts={contactUsModalTitle}
        />
        {/* <ReferralModal handleClose={handleReferralClose} open={openReferral} /> */}
        <Box textAlign="center">
          <Box
            component="img"
            src={logo}
            alt="PARCO Logo"
            width="100px"
            // height="50%"
          />
        </Box>
        <Divider />
        <Box>
          <Typography
            variant="body2"
            onClick={handleToggleProfile}
            sx={{
              marginLeft: 2,
              fontSize: '1em',
              fontWeight: 600,
              lineHeight: 1.5,
              textAlign: 'left',
              color: '#262627',
              overflowWrap: 'break-word',
              wordBreak: 'break-word',
              whiteSpace: 'normal',
              cursor: 'pointer',
            }}
          >
            Hi, {intakeState.firstName ? `${intakeState.firstName} ` : '(user)'}
          </Typography>
        </Box>
        <Box justifyContent={'space-between'} display={'flex'} px={1}>
          <Box
            display={'flex'}
            flexDirection={'row'}
            textAlign={'center'}
            alignItems={'center'}
            justifyContent={'center'}
            onClick={handleToggleProfile}
            sx={{ cursor: 'pointer' }}
          >
            <Avatar
              sx={{
                bgcolor: '#FFF',
                color: '#495057',
                borderRadius: '4px',
                left: 0,
                boxShadow: hasLogo ? 'none' : '0px 2px 2px rgba(0,0,0,0.2)',
              }}
            >
              {!hasLogo ? (
                intakeState.firstName && intakeState.lastName ? (
                  `${intakeState?.firstName?.[0]}${intakeState?.lastName?.[0]}`
                ) : (
                  <AccountCircleIcon fontSize="small" />
                )
              ) : (
                <img
                  style={{
                    maxWidth: '30px',
                    maxHeight: '30px',
                    minHeight: '30px',
                    width: 'auto',
                  }}
                  src={agencyOrCompanyLogo}
                />
              )}
            </Avatar>
            {isGovernmentEmployee && (
              <Typography
                sx={{ textAlign: 'center', color: '#495057' }}
                fontSize={'14px'}
                lineHeight={1.5}
                marginLeft={hasLogo ? 0 : 1}
              >
                {intakeState.employmentStatus === 'employed'
                  ? `${
                      selectData.agency.find(
                        ({ value }) => value === intakeState.agency
                      )?.shortcode || ''
                    } 
                Employee`
                  : 'Retired'}
              </Typography>
            )}
            {!isGovernmentEmployee && (
              <Typography
                sx={{ textAlign: 'center', color: '#495057' }}
                fontSize={'14px'}
                lineHeight={1.5}
              >
                {intakeState.employerDetails?.name}
              </Typography>
            )}
          </Box>
          <Box display="flex">
            {/* <Box display="flex">
              <IconButton
                size="large"
                aria-controls="notification-menu"
                aria-haspopup="true"
                onClick={() => null}
                sx={{
                  color: '#A3A7AA',
                  padding: '8px',
                  '&:hover': { color: '#595B59' },
                }}
              >
                <Icon>notifications</Icon>
              </IconButton>
            </Box> */}
            <Box display="flex">
              <IconButton
                size="large"
                aria-controls="notification-menu"
                aria-haspopup="true"
                onClick={handleToggleProfile}
                sx={{
                  color: '#A3A7AA',
                  padding: '8px',
                  '&:hover': { color: '#595B59' },
                }}
              >
                <Icon>settings</Icon>
              </IconButton>
              <Menu
                id="differentone"
                MenuListProps={{
                  'aria-labelledby': 'fade-button',
                }}
                anchorEl={accountMenu}
                open={accountMenuIsOpen}
                onClose={handleCloseAccountMenu}
                TransitionComponent={Fade}
              >
                <MenuItem
                  as={Link}
                  to="/profile"
                  onClick={handleCloseAccountMenu}
                >
                  <ListItemIcon>
                    <AccountCircleIcon fontSize="small" />
                  </ListItemIcon>
                  Profile
                </MenuItem>
                <MenuItem
                  onClick={
                    assumedUser
                      ? () => {
                          handleCloseAccountMenu()
                          unassume()
                        }
                      : () => {
                          handleCloseAccountMenu()
                          signOut()
                        }
                  }
                  data-testid="signout-button"
                >
                  <ListItemIcon>
                    <Lock fontSize="small" />
                  </ListItemIcon>
                  {assumedUser ? 'Back to admin account' : 'Sign Out'}
                </MenuItem>
              </Menu>
            </Box>
          </Box>
        </Box>
        <Divider />

        <List sx={{ flexGrow: '1' }}>{renderRoutes}</List>

        <Box pt={2} mt={2} px={1} bottom="0" position="relative" width="100%">
          <Box width="100%">
            <Box>
              <Button
                rel="noreferrer"
                fullWidth
                startIcon={<EventAvailable />}
                onClick={() => openModal('scheduleAppointment')}
                sx={{
                  whiteSpace: 'normal',
                  backgroundColor: 'transparent !important',
                  letterSpacing: '0.25px',
                  boxShadow: 'none',
                  color: '#A3A7AA',
                  borderRadius: '400px',
                  padding: '10px',
                  fontSize: '18px',
                  fontWeight: 'normal',
                  textTransform: 'capitalize',
                  paddingLeft: '20px',
                  justifyContent: 'flex-start',
                  '&:hover, &:active, &:focus, &:focus:not(:hover)': {
                    transform: 'scale(1.0)',
                    backgroundColor: `#f9f9f9 !important`,
                    color: '#595B59',
                  },
                  '& svg': {
                    fontSize: '24px !important',
                  },
                }}
              >
                Book a Meeting
              </Button>
            </Box>
            {/* <Box>
              <Button
                rel="noreferrer"
                fullWidth
                startIcon={<CardGiftcard />}
                onClick={() => openModal('referral')}
                sx={{
                  whiteSpace: 'normal',
                  backgroundColor: 'transparent !important',
                  letterSpacing: '0.25px',
                  boxShadow: 'none',
                  color: '#A3A7AA',
                  borderRadius: '400px',
                  padding: '10px',
                  fontSize: '18px',
                  fontWeight: 'normal',
                  textTransform: 'capitalize',
                  paddingLeft: '20px',
                  justifyContent: 'flex-start',
                  '&:hover, &:active, &:focus, &:focus:not(:hover)': {
                    transform: 'scale(1.0)',
                    backgroundColor: `#f9f9f9 !important`,
                    color: '#595B59',
                  },
                  '& svg': {
                    fontSize: '24px !important',
                    color: '#2370ED !important',
                  },
                }}
              >
                Refer a Friend
              </Button>
            </Box> */}
            <Box mb={1}>
              <Button
                rel="noreferrer"
                fullWidth
                startIcon={
                  <img
                    src={referralIcon}
                    alt="referral icon"
                    style={{ width: 20, height: 20 }}
                  />
                }
                onClick={() => openModal('referral')}
                sx={{
                  whiteSpace: 'normal',
                  backgroundColor: 'transparent !important',
                  letterSpacing: '0.25px',
                  boxShadow: 'none',
                  color: '#A3A7AA',
                  borderRadius: '400px',
                  padding: '10px',
                  fontSize: '18px',
                  fontWeight: 'normal',
                  textTransform: 'capitalize',
                  paddingLeft: '20px',
                  justifyContent: 'flex-start',
                  '&:hover, &:active, &:focus, &:focus:not(:hover)': {
                    transform: 'scale(1.0)',
                    backgroundColor: `#f9f9f9 !important`,
                    color: '#595B59',
                  },
                  '& span': {
                    marginLeft: '-2px !important',
                    marginRight: '10px !important',
                  },
                }}
              >
                Refer a Friend
              </Button>
            </Box>
            <Box mb={1}>
              <Button
                rel="noreferrer"
                fullWidth
                startIcon={<CallIcon />}
                onClick={sidebarHelp}
                sx={{
                  whiteSpace: 'normal',
                  backgroundColor: '#FFCA7B',
                  letterSpacing: '0.25px',
                  color: '#262627',
                  borderRadius: '200px',
                  padding: '10px',
                  fontSize: '18px',
                  textTransform: 'capitalize',
                  paddingLeft: '20px',
                  justifyContent: 'flex-start',
                  fontWeight: 600,
                  '&:hover, &:active, &:focus, &:focus:not(:hover)': {
                    transform: 'scale(1.0)',
                    backgroundColor: '#e5b56e',
                    color: '#262627',
                  },
                  '& svg': {
                    fontSize: '24px !important',
                  },
                }}
              >
                Just Call Me
              </Button>
            </Box>
            {/* <Box>
            <SoftButton
              rel="noreferrer"
              fullWidth
              startIcon={<AccountCircleIcon />}
              onClick={handleIssueReportModalOpen}
              sx={{
                whiteSpace: 'normal',
                backgroundColor: 'transparent !important',
                letterSpacing: '0.25px',
                boxShadow: 'none',
                color: colors.parco.buttons.transparentSidebar.text,
                borderRadius: '4px',
                padding: '10px',
                fontSize: '18px',
                fontWeight: 'normal',
                textTransform: 'capitalize',
                paddingLeft: '20px',
                justifyContent: 'flex-start',
                '&:hover, &:active, &:focus, &:focus:not(:hover)': {
                  transform: 'scale(1.0)',
                  backgroundColor: `${colors.parco.buttons.transparentSidebar.hover} !important`,
                  color: colors.parco.buttons.transparentSidebar.textHover,
                  boxShadow: 'none',
                },
              }}
            >
              Issue Report
            </SoftButton>
          </Box> */}
          </Box>
        </Box>
      </Box>
    </SidenavRoot>
  )
})

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: 'info',
  brand: '',
}

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf([
    'primary',
    'secondary',
    'info',
    'success',
    'warning',
    'error',
    'dark',
  ]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
}

export default React.memo(Sidenav)
